/**
 * CompleteOrderModal Component
 *
 * Confirmation modal for completing an order (PENDING_REVIEW -> COMPLETED)
 */

'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { Modal } from '@shared/ui';
import type { BaseModalProps } from '@shared/types/modal.types';
import { ordersApi } from '@/src/shared/api/orders.api';
import { useModal } from '@/src/shared/hooks/useModal';
import { cn } from '@/src/shared/lib/utils';
import { Typography } from '@/src/shared/ui/typography/Typography';

interface CompleteOrderModalProps extends BaseModalProps {
  orderId?: string;
  orderTitle?: string;
  workerName?: string;
  workerAvatarUrl?: string;
  agreedPrice?: number;
  onSuccess?: () => void;
}

/**
 * Complete Order Modal - allows clients to confirm order completion
 * Usage: const { open } = useModal('complete-order'); open({ orderId: '123', orderTitle: 'Task' });
 */
export function CompleteOrderModal({
  isOpen,
  onClose,
  orderId,
  orderTitle,
  workerName,
  workerAvatarUrl,
  agreedPrice,
  onSuccess,
}: CompleteOrderModalProps) {
  const t = useTranslations('profile.orders');
  const { open: openReviewModal } = useModal('create-review');

  const [isCompleting, setIsCompleting] = useState(false);

  const handleComplete = async () => {
    if (!orderId) return;

    setIsCompleting(true);

    try {
      // Update order status from PENDING_REVIEW to COMPLETED
      await ordersApi.updateStatus(orderId, 'COMPLETED');

      toast.success(t('completeSuccess'));
      onClose();
      onSuccess?.();

      // Open review modal after successful completion
      setTimeout(() => {
        openReviewModal({
          orderId,
          orderTitle,
          workerName,
          workerAvatarUrl,
        });
      }, 500); // Small delay to allow complete modal to close smoothly
    } catch (error) {
      console.error('Failed to complete order:', error);
      toast.error(t('completeError'));
    } finally {
      setIsCompleting(false);
    }
  };

  const handleClose = () => {
    if (!isCompleting) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="sm"
      animation="zoom"
      title={t('completeModal.title')}
      showCloseButton={!isCompleting}
      closeOnOverlayClick={!isCompleting}
      closeOnEsc={!isCompleting}
    >
      <div className="space-y-5">
        {/* Info message */}
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
          <svg
            className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="flex-1">
            <Typography variant="bodySm" weight="medium" color="primary" className="text-green-900 mb-1">
              {t('completeModal.confirm')}
            </Typography>
            <Typography variant="bodySm" color="secondary" className="text-green-700">
              {t('completeModal.description')}
            </Typography>
          </div>
        </div>

        {/* Order details */}
        <div className="p-3 bg-gray-50 rounded-lg space-y-2">
          {orderTitle && (
            <div>
              <Typography variant="bodySm" color="secondary" className="mb-1">
                {t('completeModal.orderTitle')}
              </Typography>
              <Typography variant="body" weight="medium" color="primary">
                {orderTitle}
              </Typography>
            </div>
          )}

          {workerName && (
            <div>
              <Typography variant="bodySm" color="secondary" className="mb-1">
                {t('completeModal.workerName')}
              </Typography>
              <Typography variant="body" weight="medium" color="primary">
                {workerName}
              </Typography>
            </div>
          )}

          {agreedPrice && (
            <div>
              <Typography variant="bodySm" color="secondary" className="mb-1">
                {t('completeModal.agreedPrice')}
              </Typography>
              <Typography variant="body" weight="medium" color="primary">
                ${agreedPrice}
              </Typography>
            </div>
          )}
        </div>

        {/* Review reminder */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Typography variant="bodySm" color="primary" className="text-blue-900">
            {t('completeModal.reviewReminder')}
          </Typography>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className={cn(
              'flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg',
              'hover:bg-gray-50 transition-colors font-medium',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            disabled={isCompleting}
          >
            {t('completeModal.cancel')}
          </button>
          <button
            type="button"
            onClick={handleComplete}
            disabled={isCompleting}
            className={cn(
              'flex-1 px-4 py-3 rounded-lg font-medium transition-colors',
              'bg-green-600 text-white',
              'hover:bg-green-700',
              'disabled:bg-gray-300 disabled:cursor-not-allowed'
            )}
          >
            {isCompleting ? t('completeModal.completing') : t('completeModal.confirmButton')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
